/**
 * Error Type Definitions
 *
 * Normalized error types for consistent error handling across the application.
 * These interfaces are used by ErrorHandlerService and consumed by thunks/services.
 *
 * Clean Architecture: Types live at the boundary between layers
 * - Thunks/services import these types
 * - Components receive processed error state from Redux, not raw errors
 */

/**
 * Data source identifiers for error context
 */
export type DataSource = 'r2' | 'atc-cdn' | 'supabase' | 'local-cache' | 'unknown';

/**
 * HTTP-like status codes for error classification
 */
export type HttpStatusCode = number;

/**
 * Fetch error classification
 * Used to determine retry strategy and user messaging
 */
export type FetchErrorType =
  | 'network'        // Network failure (offline, DNS, etc.)
  | 'timeout'        // Request timed out
  | 'not-found'      // 404 - Resource not found
  | 'server-error'   // 5xx - Server errors
  | 'client-error'   // 4xx (except 404) - Client errors
  | 'parse-error'    // JSON parse or data transformation error
  | 'unknown';       // Unclassified error

/**
 * Normalized data fetch error interface
 *
 * Used by thunks and services to report data fetching errors
 * in a consistent format to ErrorHandlerService.
 */
export interface DataFetchError {
  /** The original error that was caught */
  originalError: Error;

  /** Data source that failed (e.g., 'r2', 'atc-cdn') */
  source: DataSource;

  /** HTTP status code if available, undefined for network errors */
  statusCode?: HttpStatusCode;

  /** Classified error type for retry/recovery decisions */
  errorType: FetchErrorType;

  /** Whether this error is potentially recoverable via retry */
  retryable: boolean;

  /** Optional context data for debugging */
  context?: DataFetchErrorContext;
}

/**
 * Context data for data fetch errors
 */
export interface DataFetchErrorContext {
  /** Operation that was being performed */
  operation?: string;

  /** URL that was being fetched (may be sanitized) */
  url?: string;

  /** Time elapsed before error (ms) */
  durationMs?: number;

  /** Retry attempt number (0 = first attempt) */
  retryAttempt?: number;

  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Helper to classify a fetch error based on error properties
 *
 * @param error - The error to classify
 * @param statusCode - HTTP status code if available
 * @returns Classified error type
 */
export function classifyFetchError(
  error: Error,
  statusCode?: number
): FetchErrorType {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // HTTP status-based classification takes priority when provided
  // Status codes are more reliable than message parsing
  if (statusCode !== undefined) {
    if (statusCode === 404) {
      return 'not-found';
    }
    if (statusCode >= 500) {
      return 'server-error';
    }
    if (statusCode >= 400) {
      return 'client-error';
    }
  }

  // Network-level errors
  if (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('fetch failed') ||
    message.includes('net::') ||
    name === 'typeerror' && message.includes('fetch')
  ) {
    return 'network';
  }

  // Timeout errors
  if (
    message.includes('timeout') ||
    message.includes('timed out') ||
    name === 'aborterror' ||
    message.includes('aborted')
  ) {
    return 'timeout';
  }

  // Parse errors
  if (
    message.includes('json') ||
    message.includes('parse') ||
    message.includes('unexpected token') ||
    name === 'syntaxerror'
  ) {
    return 'parse-error';
  }

  // Check message for status codes (fallback when statusCode param not provided)
  if (message.includes('404') || message.includes('not found')) {
    return 'not-found';
  }
  if (message.includes('500') || message.includes('internal server')) {
    return 'server-error';
  }

  return 'unknown';
}

/**
 * Determine if an error type is retryable
 *
 * @param errorType - The classified error type
 * @returns Whether the error is potentially recoverable via retry
 */
export function isRetryableErrorType(errorType: FetchErrorType): boolean {
  switch (errorType) {
    case 'network':
    case 'timeout':
    case 'server-error':
      // These are typically transient and may succeed on retry
      return true;

    case 'not-found':
    case 'client-error':
    case 'parse-error':
      // These indicate issues that won't be fixed by retrying
      return false;

    case 'unknown':
    default:
      // Conservative default: allow retry for unknown errors
      return true;
  }
}

/**
 * Create a DataFetchError from a caught error
 *
 * @param error - The caught error (Error or unknown)
 * @param source - The data source that failed
 * @param context - Optional context data
 * @param statusCode - HTTP status code if available
 * @returns Normalized DataFetchError
 */
export function createDataFetchError(
  error: unknown,
  source: DataSource,
  context?: DataFetchErrorContext,
  statusCode?: number
): DataFetchError {
  const originalError = error instanceof Error
    ? error
    : new Error(String(error));

  const errorType = classifyFetchError(originalError, statusCode);

  return {
    originalError,
    source,
    statusCode,
    errorType,
    retryable: isRetryableErrorType(errorType),
    context,
  };
}

/**
 * Extract HTTP status code from various error types
 *
 * @param error - The error to extract status from
 * @returns HTTP status code or undefined
 */
export function extractStatusCode(error: unknown): number | undefined {
  if (error == null) {
    return undefined;
  }

  // Check for Response-like objects
  if (typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;
    if (typeof status === 'number') {
      return status;
    }
  }

  // Check for error with response property (axios-style)
  if (typeof error === 'object' && 'response' in error) {
    const response = (error as { response: unknown }).response;
    if (response && typeof response === 'object' && 'status' in response) {
      const status = (response as { status: unknown }).status;
      if (typeof status === 'number') {
        return status;
      }
    }
  }

  // Try to extract from error message
  if (error instanceof Error) {
    const match = error.message.match(/\b([45]\d{2})\b/);
    if (match) {
      return parseInt(match[1], 10);
    }
  }

  return undefined;
}
